import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export type UserRole = 'user' | 'maklare' | 'admin' | 'publisher' | 'agency' | 'moderator';

interface UserRoleData {
  id: string;
  user_id: string;
  role: UserRole;
  created_at: string;
}

// Simple cache to prevent duplicate requests
const roleCache = new Map<string, { role: UserRole | null; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export function useUserRoles() {
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const fetchingRef = useRef(false);

  const fetchUserRole = async () => {
    if (!user) {
      setUserRole(null);
      setLoading(false);
      return;
    }

    // Prevent duplicate requests
    if (fetchingRef.current) return;

    // Check cache first
    const cached = roleCache.get(user.id);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      setUserRole(cached.role);
      setLoading(false);
      return;
    }

    fetchingRef.current = true;

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      let role: UserRole | null = null;
      if (error) {
        console.error('Error fetching user role:', error);
        role = 'user'; // Default to user role
      } else if (data) {
        role = data.role;
      } else {
        // No role found, assign default user role
        role = 'user';
      }

      // Cache the result
      roleCache.set(user.id, { role, timestamp: Date.now() });
      setUserRole(role);
    } catch (error) {
      console.error('Error fetching user role:', error);
      setUserRole('user'); // Default to user role
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  const updateUserRole = async (newRole: UserRole, reason?: string) => {
    if (!user) return false;

    try {
      // Use secure function that requires admin privileges and logs changes
      const { data, error } = await supabase.rpc('update_user_role_secure', {
        target_user_id: user.id,
        new_role: newRole,
        reason: reason
      });

      if (error) throw error;
      
      // Update cache
      roleCache.set(user.id, { role: newRole, timestamp: Date.now() });
      setUserRole(newRole);
      return true;
    } catch (error) {
      console.error('Error updating user role:', error);
      return false;
    }
  };

  const isMaklare = () => userRole === 'maklare';
  const isAdmin = () => userRole === 'admin';
  const isPublisher = () => userRole === 'publisher';
  const isAgency = () => userRole === 'agency';
  const isModerator = () => userRole === 'moderator';
  const isModeratorOrAdmin = () => userRole === 'moderator' || userRole === 'admin';

  useEffect(() => {
    fetchUserRole();
  }, [user?.id]); // Only depend on user.id, not the entire user object

  return {
    userRole,
    loading,
    isMaklare,
    isAdmin,
    isPublisher,
    isAgency,
    isModerator,
    isModeratorOrAdmin,
    updateUserRole,
    refetch: fetchUserRole
  };
}